/**
 * The one line a scheduled-post question carries about what happens if nobody answers it.
 *
 * A marketing-plan post is asked about the day before it goes out, as a decision with a `dueBy` and
 * `display.autoApprove = {choice}` (common-lib `TaskDecisionPrompt.autoApproveChoice`). At the deadline the decision
 * service approves it with that choice when the owner's `NotificationSettings.auto_approve_scheduled_posts` allows it
 * (unset = ON), and expires it otherwise. So the card says one of two things, and only when it knows which:
 *
 *  - setting ON  → "Goes out {time} unless you change it"
 *  - setting OFF → "Won't go out unless you approve"
 *
 * `{time}` is when the post goes out — the post preview's `scheduledAt` where the card has one, else the question's
 * `dueBy`. An unknown setting (not read yet) draws NOTHING rather than guessing: claiming a post will go out to
 * someone who turned that off is the one wrong answer here.
 *
 * Pure and import-free, so it is unit-tested without the app graph (orion-web CLAUDE.md "Tests"). Shared 1:1 with
 * iOS `AutoApproveLine` and Android `autoApproveLine`.
 */

export const AUTO_APPROVE_GOES_OUT_KEY = 'tasks.decision.autoApprove.goesOut';
export const AUTO_APPROVE_WAITS_KEY = 'tasks.decision.autoApprove.waits';

export type AutoApproveLine = { key: typeof AUTO_APPROVE_GOES_OUT_KEY; at: string } | { key: typeof AUTO_APPROVE_WAITS_KEY };

/** The settings field as read: `undefined` = unset (ON); pass `null` while the settings have not been read. */
export function autoApproveSettingOn(autoApproveScheduledPosts: boolean | undefined): boolean {
  return autoApproveScheduledPosts !== false;
}

export function autoApproveLine(
  prompt: { autoApproveChoice?: string; dueBy?: string } | undefined,
  postAt: string | undefined,
  settingOn: boolean | null,
): AutoApproveLine | null {
  if (!prompt?.autoApproveChoice || settingOn === null) return null;
  if (!settingOn) return { key: AUTO_APPROVE_WAITS_KEY };
  const at = [postAt, prompt.dueBy].find((value) => !!value && !Number.isNaN(Date.parse(value)));
  return at ? { key: AUTO_APPROVE_GOES_OUT_KEY, at } : null;
}

/**
 * `{time}` as the reader reads it: the clock time alone when it is today, else the short weekday and the time
 * ("Fri 9:00 AM"). The question is asked about a day ahead, so a weekday is never ambiguous.
 */
export function autoApproveTime(iso: string, now: Date, locale?: string, timeZone?: string): string {
  const at = new Date(iso);
  const sameDay = (a: Date, b: Date) => a.toLocaleDateString('en-CA', { timeZone }) === b.toLocaleDateString('en-CA', { timeZone });
  const time: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZone };
  return sameDay(at, now) ? at.toLocaleTimeString(locale, time) : at.toLocaleString(locale, { weekday: 'short', ...time });
}

/**
 * The open question a chat's post card is about, found on the task it names — the chat card itself carries only the
 * ids. Undefined once that question is no longer on the task (answered, expired or auto-approved), so a settled post's
 * card in the chat says nothing about what "will" happen.
 */
export function cardDecisionPrompt<P extends { decisionId: string }>(
  tasks: readonly { id?: string; decisionPrompts?: readonly P[] }[] | undefined,
  card: { taskId?: string; decisionId?: string },
): P | undefined {
  if (!card.decisionId || !card.taskId) return undefined;
  return tasks?.find((task) => task.id === card.taskId)?.decisionPrompts?.find((prompt) => prompt.decisionId === card.decisionId);
}
